import { UseFormReturn, useWatch } from "react-hook-form";
import { Container } from "react-bootstrap";
import { useRouteContext } from "@tanstack/react-router";

import { useENRLFormLogic } from "../../useENRLFormLogic";
import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import Select from "@/components/shared/forms/Select";
import DateInput from "@/components/shared/forms/DateInput";
import TextInput from "@/components/shared/forms/TextInput";
import { type ENRL } from "@/models.ts";
import { useCoreData } from "@/queries.ts";
import { FiFileText } from "react-icons/fi";

type PreschoolSummary = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

type ENRLWizardFormProps = {
  form: UseFormReturn<ENRL, any, ENRL>;
  preschools: PreschoolSummary[];
};

export function ENRLWizardForm({ form, preschools }: ENRLWizardFormProps) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const tooltipMap = leaContext.coreData.tooltipMap.get("ENRL");
  const coreData = useCoreData();

  const { endDateRules, endReasonRules } = useENRLFormLogic(form);

  const {
    register,
    formState: { errors },
  } = form;

  const enrollmentEndDate = useWatch({
    control: form.control,
    name: "EnrollmentEndDate",
  });

  return (
    <Container
      className="ps-3 pe-3 overview-container"
      style={{ borderTop: "none", borderRadius: 0 }}
    >
      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FiFileText className="section-icon" />
          <h3 className="section-title">Enrollment Information</h3>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="CSPPID"
              labelText="CSPPID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  readOnly
                  maxLength={50}
                  {...register(name)}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="LocalChildId"
              labelText="Local Child ID (CICN)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  readOnly
                  maxLength={50}
                  {...register(name, {
                    required: "Local Child ID (CICN) is required",
                  })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="ProgramYear"
              labelText="Program Year"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <ReadOnlySelect
                  id={id}
                  control={form.control}
                  name={name}
                  rules={{ required: "Program Year is required" }}
                  options={coreData.programYearList}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="PreschoolCode"
              labelText="Preschool"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <ReadOnlySelect
                  id={id}
                  control={form.control}
                  name={name}
                  rules={{ required: "Preschool is required" }}
                  options={preschools.map((x) => ({
                    Text: x.PreschoolName,
                    Value: x.PreschoolCDSCode,
                  }))}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="ContractType"
              labelText="Contract Type"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={false}
                  {...register(name, { required: "Contract Year is required" })}
                  options={coreData.contractTypesOfferedList}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ProgramType"
              labelText="Program Type"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={false}
                  {...register(name, { required: "Program Type is required" })}
                  options={coreData.programTypeList}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="ProgramDuration"
              labelText="Program Duration"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={false}
                  {...register(name)}
                  options={coreData.programDurationList}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="EligibilityStatus"
              labelText="Eligibility Status"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={false}
                  {...register(name, {
                    required: "Eligiblity Status is required",
                  })}
                  options={coreData.eligibilityStatusList}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="EnrollmentStartDate"
              labelText="Enrollment Start Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <DateInput
                  id={id}
                  disabled={false}
                  max="9999-12-31"
                  {...register("EnrollmentStartDate", {
                    required: "Enrollment Start Date is required",
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="EnrollmentEndDate"
              labelText="Enrollment End Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <DateInput
                  id={id}
                  disabled={false}
                  max="9999-12-31"
                  {...register(name, endDateRules)}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-6">
            <FormFieldRow
              name="EnrollmentEndReason"
              labelText="Enrollment End Reason"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={!!enrollmentEndDate}
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={false}
                  {...register(name, endReasonRules)}
                  options={coreData.studentExitCategoryList}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>
    </Container>
  );
}
